import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { format, addDays, startOfDay, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';
import { deliverableService } from '../../../services/deliverableService';
import { formatDisplayDate } from '../../../utils/dateUtils';

const STATUS_CONFIG = {
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
  'in-progress': { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  'in_progress': { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  'not-started': { label: 'Not Started', color: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
  'on-hold': { label: 'On Hold', color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
};

const getStatusConfig = (status, endDate) => {
  const today = startOfDay(new Date());
  const end = endDate ? startOfDay(parseISO(endDate)) : null;
  if (status === 'completed') return STATUS_CONFIG?.completed;
  if (end && end < today && status !== 'completed') return STATUS_CONFIG?.overdue;
  const key = status?.toLowerCase()?.replace(/\s+/g, '-');
  return STATUS_CONFIG?.[key] || STATUS_CONFIG?.['not-started'];
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  return formatDisplayDate(dateStr);
};

const ROW_TYPE_LABEL = {
  deliverable: 'Deliverable',
  task: 'Task',
  subtask: 'Subtask',
};

const ROW_TYPE_ICON = {
  deliverable: 'Package',
  task: 'CheckSquare',
  subtask: 'Minus',
};

const SORT_OPTIONS = [
  { value: 'defined_order', label: 'Defined Order' },
  { value: 'start_date', label: 'Start Date ↑' },
  { value: 'end_date', label: 'End Date ↑' },
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'start_desc', label: 'Start Date ↓' },
  { value: 'end_desc', label: 'End Date ↓' },
  { value: 'status_asc', label: 'Status (A–Z)' },
];

const RESOURCE_SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'start_asc', label: 'Start Date ↑' },
  { value: 'start_desc', label: 'Start Date ↓' },
  { value: 'end_asc', label: 'End Date ↑' },
  { value: 'end_desc', label: 'End Date ↓' },
  { value: 'category_asc', label: 'Category (A–Z)' },
];

const FilterSelect = ({ label, value, onChange, options, icon }) => (
  <div className="flex items-center gap-1.5">
    {icon && <Icon name={icon} size={13} className="text-muted-foreground flex-shrink-0" />}
    <select
      value={value}
      onChange={(e) => onChange(e?.target?.value)}
      className="text-xs border border-border rounded-md px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent cursor-pointer min-w-[130px]"
    >
      <option value="">{label}</option>
      {options?.map((o) => (
        <option key={o?.value} value={o?.value}>{o?.label}</option>
      ))}
    </select>
  </div>
);

const ProjectTimeline = ({ deliverables = [], projectAssignments = [], resources = [], phases = [], project, onReorder, userId, onAdjustDates }) => {
  const today = startOfDay(new Date());
  const windowStart = addDays(today, -30);
  const windowEnd = addDays(today, 90);
  const windowLabel = `${format(windowStart, 'MMM d, yyyy')} – ${format(windowEnd, 'MMM d, yyyy')}`;

  // --- Deliverable/Task filters & sort ---
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortDel, setSortDel] = useState('start_asc');
  const [sortLoaded, setSortLoaded] = useState(false);

  // --- Resource filters & sort ---
  const [filterResCategory, setFilterResCategory] = useState('');
  const [sortRes, setSortRes] = useState('start_asc');

  // --- Active section tab ---
  const [activeSection, setActiveSection] = useState('deliverables');

  // --- Collapsed phases ---
  const [collapsedPhases, setCollapsedPhases] = useState({});

  // --- Drag state ---
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemRef = useRef(null);

  // Touch drag state for mobile long-press
  const touchDragItemRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const [touchDragActive, setTouchDragActive] = useState(false);
  const [touchDragOverId, setTouchDragOverId] = useState(null);

  // Load sort preference from Supabase on mount
  useEffect(() => {
    const loadSortPref = async () => {
      if (!userId || !project?.id) { setSortLoaded(true); return; }
      const { data } = await deliverableService?.getSortPreference(userId, project?.id);
      // Map Supabase sort keys to timeline sort keys
      if (data === 'start_date') setSortDel('start_asc');
      else if (data === 'end_date') setSortDel('end_asc');
      else if (data === 'defined_order') setSortDel('defined_order');
      setSortLoaded(true);
    };
    loadSortPref();
  }, [userId, project?.id]);

  // Save sort preference when it changes (after initial load)
  useEffect(() => {
    if (!sortLoaded || !userId || !project?.id) return;
    // Map timeline sort keys back to Supabase keys
    let supabaseKey = 'defined_order';
    if (sortDel === 'start_asc' || sortDel === 'start_desc') supabaseKey = 'start_date';
    else if (sortDel === 'end_asc' || sortDel === 'end_desc') supabaseKey = 'end_date';
    deliverableService?.saveSortPreference(userId, project?.id, supabaseKey);
  }, [sortDel, sortLoaded, userId, project?.id]);

  // ── Touch drag helpers (long-press for mobile) ──────────────────────────
  const triggerHaptic = (type = 'light') => {
    if (navigator?.vibrate) {
      navigator.vibrate(type === 'light' ? 30 : type === 'medium' ? 60 : 100);
    }
  };

  const handleTouchStart = useCallback((e, item) => {
    longPressTimerRef.current = setTimeout(() => {
      touchDragItemRef.current = item;
      setTouchDragActive(true);
      triggerHaptic('medium');
    }, 500);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchDragItemRef?.current) {
      clearTimeout(longPressTimerRef?.current);
      return;
    }
    e?.preventDefault();
    const touch = e?.touches?.[0];
    if (!touch) return;
    const el = document?.elementFromPoint(touch?.clientX, touch?.clientY);
    const rowEl = el?.closest('[data-row-id]');
    const targetId = rowEl?.getAttribute('data-row-id');
    setTouchDragOverId(targetId || null);
  }, []);

  const handleTouchEnd = useCallback(async (e) => {
    clearTimeout(longPressTimerRef?.current);
    if (!touchDragItemRef?.current || !touchDragOverId) {
      touchDragItemRef.current = null;
      setTouchDragActive(false);
      setTouchDragOverId(null);
      return;
    }
    const draggedItem = touchDragItemRef?.current;
    const targetId = touchDragOverId;
    touchDragItemRef.current = null;
    setTouchDragActive(false);
    setTouchDragOverId(null);

    if (draggedItem?.id === targetId) return;
    const targetItem = deliverables?.find(d => d?.id === targetId);
    if (!targetItem) return;

    const draggedType = draggedItem?.rowType || 'deliverable';
    const targetType = targetItem?.rowType || 'deliverable';
    if (draggedType !== targetType) return;
    if (draggedItem?.parentId !== targetItem?.parentId) return;

    const siblings = [...deliverables?.filter(d => {
      const dt = d?.rowType || 'deliverable';
      return dt === draggedType && d?.parentId === draggedItem?.parentId;
    })]?.sort((a, b) => {
      const ao = a?.customOrder ?? Infinity;
      const bo = b?.customOrder ?? Infinity;
      return ao !== bo ? ao - bo : (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
    });

    const dragIdx = siblings?.findIndex(s => s?.id === draggedItem?.id);
    const targetIdx = siblings?.findIndex(s => s?.id === targetItem?.id);
    if (dragIdx === -1 || targetIdx === -1) return;

    const reordered = [...siblings];
    reordered?.splice(dragIdx, 1);
    reordered?.splice(targetIdx, 0, draggedItem);

    const updates = reordered?.map((item, idx) => {
      let newOrder;
      if (draggedType === 'deliverable') {
        newOrder = idx + 1;
      } else if (draggedType === 'task') {
        const parent = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parent?.customOrder != null ? Math.floor(Number(parent?.customOrder)) : 1;
        newOrder = parseFloat(`${parentOrder}.${idx + 1}`);
      } else if (draggedType === 'subtask') {
        const parentTask = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parentTask?.customOrder != null ? Number(parentTask?.customOrder) : 1.1;
        const parentStr = parentOrder?.toFixed(1);
        newOrder = parseFloat(`${parentStr}${idx + 1}`);
      }
      return { id: item?.id, customOrder: newOrder };
    });

    triggerHaptic('light');
    await onReorder?.(updates);
  }, [touchDragOverId, deliverables, onReorder]);

  const togglePhase = (phaseKey) => {
    setCollapsedPhases((prev) => ({ ...prev, [phaseKey]: !prev?.[phaseKey] }));
  };

  // Phase map
  const phaseMap = useMemo(() => {
    const map = {};
    for (const phase of phases) map[phase.id] = phase;
    return map;
  }, [phases]);

  // Filter deliverables in window
  const windowDeliverables = useMemo(() => {
    // Show ALL deliverables/tasks/subtasks — no date or window filtering
    return deliverables || [];
  }, [deliverables]);

  // Filter resource assignments in window
  const windowAssignments = useMemo(() => {
    return projectAssignments?.filter((a) => {
      const start = a?.startDate ? startOfDay(parseISO(a?.startDate)) : null;
      const end = a?.endDate ? startOfDay(parseISO(a?.endDate)) : null;
      return (!start || start <= windowEnd) && (!end || end >= windowStart);
    });
  }, [projectAssignments, windowStart, windowEnd]);

  // Unique statuses for filter
  const statusOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const d of windowDeliverables) {
      const sc = getStatusConfig(d?.status, d?.endDate);
      if (!seen?.has(sc?.label)) { seen?.add(sc?.label); opts?.push({ value: sc?.label, label: sc?.label }); }
    }
    return opts?.sort((a, b) => a?.label?.localeCompare(b?.label));
  }, [windowDeliverables]);

  // Unique types
  const typeOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const d of windowDeliverables) {
      const t = d?.rowType || 'deliverable';
      if (!seen?.has(t)) { seen?.add(t); opts?.push({ value: t, label: ROW_TYPE_LABEL?.[t] || t }); }
    }
    return opts;
  }, [windowDeliverables]);

  // Unique resource categories
  const categoryOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const a of windowAssignments) {
      const cat = a?.resource?.category;
      if (cat && !seen?.has(cat)) { seen?.add(cat); opts?.push({ value: cat, label: cat }); }
    }
    return opts?.sort((a, b) => a?.label?.localeCompare(b?.label));
  }, [windowAssignments]);

  // Apply deliverable filters (no phase filter — grouped inline)
  const filteredDeliverables = useMemo(() => {
    let items = [...windowDeliverables];
    if (filterStatus) items = items?.filter((d) => getStatusConfig(d?.status, d?.endDate)?.label === filterStatus);
    if (filterType) items = items?.filter((d) => (d?.rowType || 'deliverable') === filterType);
    // Sort within each phase group
    items?.sort((a, b) => {
      switch (sortDel) {
        case 'defined_order': {
          const ao = a?.customOrder ?? Infinity;
          const bo = b?.customOrder ?? Infinity;
          return ao !== bo ? ao - bo : (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
        }
        case 'start_date': case'start_asc': return (a?.startDate || '')?.localeCompare(b?.startDate || '');
        case 'name_asc': return (a?.name || '')?.localeCompare(b?.name || '');
        case 'name_desc': return (b?.name || '')?.localeCompare(a?.name || '');
        case 'start_desc': return (b?.startDate || '')?.localeCompare(a?.startDate || '');
        case 'end_date': case'end_asc': return (a?.endDate || '')?.localeCompare(b?.endDate || '');
        case 'end_desc': return (b?.endDate || '')?.localeCompare(a?.endDate || '');
        case 'status_asc': return (getStatusConfig(a?.status, a?.endDate)?.label || '')?.localeCompare(getStatusConfig(b?.status, b?.endDate)?.label || '');
        default: return 0;
      }
    });
    return items;
  }, [windowDeliverables, filterStatus, filterType, sortDel]);

  // Group filtered deliverables by phase, preserving phase order
  const groupedByPhase = useMemo(() => {
    const groups = new Map();
    // First pass: collect phase keys in order
    for (const item of filteredDeliverables) {
      const phaseKey = item?.phaseId || '__unassigned__';
      if (!groups?.has(phaseKey)) {
        groups?.set(phaseKey, []);
      }
      groups?.get(phaseKey)?.push(item);
    }
    // Sort groups: named phases first (by phase order if available), then unassigned
    const sortedGroups = [];
    // Add phases in their defined order first
    for (const phase of phases) {
      if (groups?.has(phase?.id)) {
        sortedGroups?.push({ phaseKey: phase?.id, phaseName: phase?.name, items: groups?.get(phase?.id) });
      }
    }
    // Add unassigned last
    if (groups?.has('__unassigned__')) {
      sortedGroups?.push({ phaseKey: '__unassigned__', phaseName: 'Unassigned', items: groups?.get('__unassigned__') });
    }
    return sortedGroups;
  }, [filteredDeliverables, phases]);

  // Apply resource filters
  const filteredAssignments = useMemo(() => {
    let items = [...windowAssignments];
    if (filterResCategory) items = items?.filter((a) => a?.resource?.category === filterResCategory);
    items?.sort((a, b) => {
      switch (sortRes) {
        case 'name_asc': return (a?.resource?.name || '')?.localeCompare(b?.resource?.name || '');
        case 'name_desc': return (b?.resource?.name || '')?.localeCompare(a?.resource?.name || '');
        case 'start_asc': return (a?.startDate || '')?.localeCompare(b?.startDate || '');
        case 'start_desc': return (b?.startDate || '')?.localeCompare(a?.startDate || '');
        case 'end_asc': return (a?.endDate || '')?.localeCompare(b?.endDate || '');
        case 'end_desc': return (b?.endDate || '')?.localeCompare(a?.endDate || '');
        case 'category_asc': return (a?.resource?.category || '')?.localeCompare(b?.resource?.category || '');
        default: return 0;
      }
    });
    return items;
  }, [windowAssignments, filterResCategory, sortRes]);

  if (windowDeliverables?.length === 0 && windowAssignments?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
        <Icon name="CalendarX" size={32} className="mb-2 opacity-40" />
        <p className="text-sm font-medium">No deliverables or assignments in this window</p>
        <p className="text-xs mt-1">{windowLabel}</p>
      </div>
    );
  }

  // ── Row drag handlers ──
  const handleRowDragStart = (e, item) => {
    dragItemRef.current = item;
    e?.dataTransfer?.setData('text/plain', item?.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRowDragOver = (e, item) => {
    e?.preventDefault();
    setDragOverId(item?.id);
  };

  const handleRowDrop = async (e, targetItem) => {
    e?.preventDefault();
    setDragOverId(null);
    const draggedItem = dragItemRef?.current;
    if (!draggedItem || draggedItem?.id === targetItem?.id) return;

    const draggedType = draggedItem?.rowType || 'deliverable';
    const targetType = targetItem?.rowType || 'deliverable';
    if (draggedType !== targetType) return;
    if (draggedItem?.parentId !== targetItem?.parentId) return;

    const siblings = [...deliverables?.filter(d => {
      const dt = d?.rowType || 'deliverable';
      return dt === draggedType && d?.parentId === draggedItem?.parentId;
    })]?.sort((a, b) => {
      const ao = a?.customOrder ?? Infinity;
      const bo = b?.customOrder ?? Infinity;
      return ao !== bo ? ao - bo : (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
    });

    const dragIdx = siblings?.findIndex(s => s?.id === draggedItem?.id);
    const targetIdx = siblings?.findIndex(s => s?.id === targetItem?.id);
    if (dragIdx === -1 || targetIdx === -1) return;

    const reordered = [...siblings];
    reordered?.splice(dragIdx, 1);
    reordered?.splice(targetIdx, 0, draggedItem);

    const updates = reordered?.map((item, idx) => {
      let newOrder;
      if (draggedType === 'deliverable') {
        newOrder = idx + 1;
      } else if (draggedType === 'task') {
        const parent = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parent?.customOrder != null ? Math.floor(Number(parent?.customOrder)) : 1;
        newOrder = parseFloat(`${parentOrder}.${idx + 1}`);
      } else if (draggedType === 'subtask') {
        const parentTask = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parentTask?.customOrder != null ? Number(parentTask?.customOrder) : 1.1;
        const parentStr = parentOrder?.toFixed(1);
        newOrder = parseFloat(`${parentStr}${idx + 1}`);
      }
      return { id: item?.id, customOrder: newOrder };
    });

    await onReorder?.(updates);
    dragItemRef.current = null;
  };

  const handleRowDragEnd = () => {
    setDragOverId(null);
    dragItemRef.current = null;
  };

  return (
    <div className="space-y-3">
      {/* Project identifier */}
      {project && (
        <div className="flex items-center gap-2 px-1">
          <Icon name="Briefcase" size={13} className="text-primary flex-shrink-0" />
          {project?.projectNumber && (
            <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              #{project?.projectNumber}
            </span>
          )}
          <span className="text-sm font-semibold text-foreground">{project?.projectName || project?.name}</span>
        </div>
      )}
      {/* Section Tabs */}
      <div className="flex items-center gap-1 border-b border-border/60 pb-0">
        <button
          onClick={() => setActiveSection('deliverables')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-t-md border-b-2 transition-colors ${
            activeSection === 'deliverables' ? 'border-accent text-accent bg-accent/5' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Package" size={13} />
          Deliverables &amp; Tasks
          <span className="ml-0.5 text-muted-foreground font-normal">({windowDeliverables?.length})</span>
        </button>
        <button
          onClick={() => setActiveSection('resources')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-t-md border-b-2 transition-colors ${
            activeSection === 'resources' ? 'border-accent text-accent bg-accent/5' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Users" size={13} />
          Resource Assignments
          <span className="ml-0.5 text-muted-foreground font-normal">({windowAssignments?.length})</span>
        </button>
        {onAdjustDates && (
          <button
            onClick={onAdjustDates}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-t-md border-b-2 border-transparent text-accent hover:text-accent/80 hover:bg-accent/5 transition-colors ml-auto"
            title="Adjust all project dates"
          >
            <Icon name="CalendarClock" size={13} />
            Adjust Dates
          </button>
        )}
      </div>
      {/* Deliverables & Tasks Grid */}
      {activeSection === 'deliverables' && (
        <div className="space-y-2">
          {/* Filters & Sort Bar */}
          <div className="flex flex-wrap items-center gap-2 bg-muted/30 rounded-lg px-3 py-2 border border-border/50">
            <Icon name="Filter" size={13} className="text-muted-foreground flex-shrink-0" />
            <span className="text-xs font-medium text-muted-foreground mr-1">Filter:</span>
            <FilterSelect
              label="All Statuses"
              value={filterStatus}
              onChange={setFilterStatus}
              options={statusOptions}
              icon=""
            />
            <FilterSelect
              label="All Types"
              value={filterType}
              onChange={setFilterType}
              options={typeOptions}
              icon=""
            />
            <div className="flex items-center gap-1.5 ml-auto">
              <Icon name="ArrowUpDown" size={13} className="text-muted-foreground flex-shrink-0" />
              <span className="text-xs font-medium text-muted-foreground">Sort:</span>
              <FilterSelect
                label="Sort by…"
                value={sortDel}
                onChange={setSortDel}
                options={SORT_OPTIONS}
                icon=""
              />
              {/* Active sort badge */}
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border ${
                sortDel === 'defined_order' ?'bg-purple-100 text-purple-700 border-purple-200'
                  : sortDel === 'start_asc'|| sortDel === 'start_date' || sortDel === 'start_desc' ?'bg-blue-100 text-blue-700 border-blue-200'
                  : sortDel === 'end_asc'|| sortDel === 'end_date' || sortDel === 'end_desc' ?'bg-amber-100 text-amber-700 border-amber-200' :'bg-muted text-muted-foreground border-border'
              }`}>
                <Icon name="Check" size={10} />
                {SORT_OPTIONS?.find(o => o?.value === sortDel)?.label || sortDel}
              </span>
            </div>
            {(filterStatus || filterType) && (
              <button
                onClick={() => { setFilterStatus(''); setFilterType(''); }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground ml-1 px-1.5 py-0.5 rounded border border-border hover:bg-muted transition-colors"
              >
                <Icon name="X" size={11} />
                Clear
              </button>
            )}
          </div>

          {filteredDeliverables?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Icon name="SearchX" size={24} className="mb-2 opacity-40" />
              <p className="text-xs">No items match the current filters</p>
            </div>
          ) : (
            <>
              {/* Grid Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-muted/50 rounded-md border border-border/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                <div className="col-span-5">Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-3 text-right">Dates</div>
              </div>

              {/* Grid Rows — grouped by phase */}
              <div className="space-y-0.5">
                {groupedByPhase?.map(({ phaseKey, phaseName, items }) => {
                  const isCollapsed = collapsedPhases?.[phaseKey];
                  return (
                    <div key={phaseKey}>
                      {/* Inline Phase Header Row */}
                      <button
                        onClick={() => togglePhase(phaseKey)}
                        className="w-full grid grid-cols-12 gap-2 px-3 py-1.5 bg-muted/60 border border-border/60 rounded-md items-center hover:bg-muted/80 transition-colors mt-1 mb-0.5"
                      >
                        <div className="col-span-12 flex items-center gap-2">
                          <Icon
                            name={isCollapsed ? 'ChevronRight' : 'ChevronDown'}
                            size={12}
                            className="text-muted-foreground flex-shrink-0"
                          />
                          <Icon name="Layers" size={12} className="text-accent flex-shrink-0" />
                          <span className="text-xs font-semibold text-foreground">{phaseName}</span>
                          <span className="text-xs text-muted-foreground font-normal ml-1">({items?.length} item{items?.length !== 1 ? 's' : ''})</span>
                        </div>
                      </button>

                      {/* Phase Items */}
                      {!isCollapsed && items?.map((item) => {
                        const sc = getStatusConfig(item?.status, item?.endDate);
                        const rowType = item?.rowType || 'deliverable';
                        const iconName = ROW_TYPE_ICON?.[rowType] || 'Package';
                        const typeLabel = ROW_TYPE_LABEL?.[rowType] || rowType;
                        const indentClass = rowType === 'subtask' ? 'pl-10' : rowType === 'task' ? 'pl-5' : 'pl-0';
                        const isDragOver = dragOverId === item?.id || touchDragOverId === item?.id;
                        const isTouchDragging = touchDragActive && touchDragItemRef?.current?.id === item?.id;
                        return (
                          <div
                            key={item?.id}
                            data-row-id={item?.id}
                            draggable
                            onDragStart={(e) => handleRowDragStart(e, item)}
                            onDragOver={(e) => handleRowDragOver(e, item)}
                            onDrop={(e) => handleRowDrop(e, item)}
                            onDragEnd={handleRowDragEnd}
                            onTouchStart={(e) => handleTouchStart(e, item)}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            className={`grid grid-cols-12 gap-2 px-3 py-2 rounded-md hover:bg-muted/40 transition-colors border items-center cursor-grab active:cursor-grabbing select-none ${isDragOver ? 'border-primary border-t-2 bg-primary/5' : 'border-transparent hover:border-border/40'} ${isTouchDragging ? 'opacity-50 scale-95' : ''}`}
                          >
                            {/* Name */}
                            <div className={`col-span-5 flex items-center gap-2 min-w-0 ${indentClass}`}>
                              <Icon name="GripVertical" size={12} className="text-muted-foreground/50 flex-shrink-0" />
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sc?.dot}`} />
                              <Icon name={iconName} size={12} className="text-muted-foreground flex-shrink-0" />
                              <span className="text-xs font-medium text-foreground truncate">{item?.name}</span>
                            </div>
                            {/* Type */}
                            <div className="col-span-2">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border/60 whitespace-nowrap">
                                {typeLabel}
                              </span>
                            </div>
                            {/* Status */}
                            <div className="col-span-2">
                              <span className={`text-xs px-1.5 py-0.5 rounded border whitespace-nowrap ${sc?.color}`}>
                                {sc?.label}
                              </span>
                            </div>
                            {/* Dates */}
                            <div className="col-span-3 text-right">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDateShort(item?.startDate)}{item?.endDate ? ` – ${formatDateShort(item?.endDate)}` : ''}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground pt-1">
                Showing <span className="font-medium">{filteredDeliverables?.length}</span> of <span className="font-medium">{windowDeliverables?.length}</span> items
              </p>
            </>
          )}
        </div>
      )}
      {/* Resource Assignments Grid */}
      {activeSection === 'resources' && (
        <div className="space-y-2">
          {/* Filters & Sort Bar */}
          <div className="flex flex-wrap items-center gap-2 bg-muted/30 rounded-lg px-3 py-2 border border-border/50">
            <Icon name="Filter" size={13} className="text-muted-foreground flex-shrink-0" />
            <span className="text-xs font-medium text-muted-foreground mr-1">Filter:</span>
            <FilterSelect
              label="All Categories"
              value={filterResCategory}
              onChange={setFilterResCategory}
              options={categoryOptions}
              icon=""
            />
            <div className="flex items-center gap-1.5 ml-auto">
              <Icon name="ArrowUpDown" size={13} className="text-muted-foreground flex-shrink-0" />
              <span className="text-xs font-medium text-muted-foreground">Sort:</span>
              <FilterSelect
                label="Sort by…"
                value={sortRes}
                onChange={setSortRes}
                options={RESOURCE_SORT_OPTIONS}
                icon=""
              />
            </div>
            {filterResCategory && (
              <button
                onClick={() => setFilterResCategory('')}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground ml-1 px-1.5 py-0.5 rounded border border-border hover:bg-muted transition-colors"
              >
                <Icon name="X" size={11} />
                Clear
              </button>
            )}
          </div>

          {filteredAssignments?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Icon name="SearchX" size={24} className="mb-2 opacity-40" />
              <p className="text-xs">No assignments match the current filters</p>
            </div>
          ) : (
            <>
              {/* Grid Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-muted/50 rounded-md border border-border/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                <div className="col-span-4">Resource</div>
                <div className="col-span-3">Category</div>
                <div className="col-span-3">Start Date</div>
                <div className="col-span-2">End Date</div>
              </div>
              {/* Grid Rows */}
              <div className="space-y-1">
                {filteredAssignments?.map((a, idx) => {
                  const resource = a?.resource;
                  return (
                    <div
                      key={`${a?.id}-${idx}`}
                      className="grid grid-cols-12 gap-2 px-3 py-2 rounded-md hover:bg-muted/40 transition-colors border border-transparent hover:border-border/40 items-center"
                    >
                      {/* Resource Name */}
                      <div className="col-span-4 flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                        <Icon name="User" size={12} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-xs font-medium text-foreground truncate">{resource?.name || 'Unknown'}</span>
                      </div>
                      {/* Category */}
                      <div className="col-span-3">
                        {resource?.category ? (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border/60 whitespace-nowrap">
                            {resource?.category}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                      {/* Start Date */}
                      <div className="col-span-3">
                        <span className="text-xs text-muted-foreground">{formatDateShort(a?.startDate)}</span>
                      </div>
                      {/* End Date */}
                      <div className="col-span-2">
                        <span className="text-xs text-muted-foreground">{formatDateShort(a?.endDate)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Showing <span className="font-medium">{filteredAssignments?.length}</span> of <span className="font-medium">{windowAssignments?.length}</span> assignments
              </p>
            </>
          )}
        </div>
      )}
      {/* Footnote */}
      <p className="text-xs text-muted-foreground border-t border-border/60 pt-2 mt-1">
        Window: <span className="font-medium">{windowLabel}</span>
      </p>
    </div>
  );
};

export default ProjectTimeline;
